import { Component, Input, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { AiPresenceTopicsTableResponse, AiPresenceTopicsTableRowData } from '../types';
import { NoWidgetDataComponent } from '../../my-brand-screen/no-widget-data/no-widget-data.component';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { CommonModule } from '@angular/common';
import { AgGridAngular } from 'ag-grid-angular';
import { CellStyleModule, ClientSideRowModelModule, ColDef, EventApiModule, GridApi, GridOptions, GridReadyEvent, themeQuartz } from 'ag-grid-community';
import { SortClickEvent } from '../../../utils/sort-state';
import { RowSelectionModule } from 'ag-grid-community';
import { TableHeaderCellComponent } from '../../../shared/components/table-header-cell/table-header-cell.component';
import { PerformanceTrendRendererComponent } from '../../../shared/components/performance-trend-renderer/performance-trend-renderer.component';
import { addPercent } from '../../../utils/numbers';
import { ModuleRegistry } from 'ag-grid-community'; 
import { ColumnApiModule } from 'ag-grid-community'; 
import { RenderApiModule } from 'ag-grid-community'; 
import { ValueType } from '../../../shared/components/overview-tiles/types';

ModuleRegistry.registerModules([
  CellStyleModule,
  ClientSideRowModelModule,
  RenderApiModule,
  ColumnApiModule,
  RowSelectionModule,
  EventApiModule
]);

@Component({
  selector: 'app-topics-table',
  imports: [
    CommonModule,
    LoaderComponent,
    NoWidgetDataComponent,
    AgGridAngular
  ],
  templateUrl: './topics-table.component.html',
  styleUrl: './topics-table.component.scss'
})
export class TopicsTableComponent implements OnChanges {
  @Input() loading = false;
  @Input() data?: AiPresenceTopicsTableResponse;

  protected rowData?: AiPresenceTopicsTableRowData;
  protected gridApi?: GridApi;
  protected colDefs: ColDef[] = [];
  protected modules = [ClientSideRowModelModule];

  protected theme = themeQuartz.withParams({
    headerBackgroundColor: '#EBEDEF',
    rowHoverColor: 'rgba(233, 244, 245, 0.85)',
    rowHeight: 56,
    headerHeight: 46,
    fontSize: '14px',
    headerFontSize: '14px',
    fontFamily: 'Poppins',
    headerFontFamily: 'Poppins',
    headerFontWeight: 600,
    headerTextColor: '#4E5258',
    cellTextColor: '#4E5258',
    headerRowBorder: { style: 'solid', color: '#DEE0E4' },
    headerColumnBorder: { style: 'solid', color: '#DEE0E4' },
    rowBorder: { style: 'solid', color: '#DEE0E4' },
    columnBorder: { style: 'solid', color: '#DEE0E4' },
  });

  protected gridOptions: GridOptions = {
    suppressMovableColumns: true,
    suppressCellFocus: true,
    domLayout: 'autoHeight',
    defaultColDef: {
      sortable: true,
      resizable: false,
      filter: false,
      wrapHeaderText: true,
      autoHeaderHeight: true,
    },
  };

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.gridApi.addEventListener('sortClick' as any, this.onSortChanged.bind(this));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.data) {
      this.rowData = this.getRowData();
      this.colDefs = this.getColDef();
    }
  }

  private getRowData(): AiPresenceTopicsTableRowData {
    return {
      topics: this.data?.topics.map(topic => {
        const { visibility, sentiment, topCompetitors } = topic;
        const isVisibilityInvalid = !visibility && !topCompetitors;
        const isSentimentInvalid = !sentiment;

        const visibilityContainerStyle = { display: 'flex', gap: '16px', justifyContent: isVisibilityInvalid ? 'flex-start' : 'center'};
        const sentimentContainerStyle = { display: 'flex', gap: '16px', justifyContent: isSentimentInvalid ? 'flex-start' : 'center', ...( isSentimentInvalid ? { paddingLeft: '16px' } : {}) };
        
        return {
            topic: topic.topic,
            visibility: {
              containerStyle: visibilityContainerStyle,
              includeScoreBar: isVisibilityInvalid ? false : true,
              value: isVisibilityInvalid ? ValueType.NA : visibility ? addPercent(visibility.value) : ValueType.NA,
              trend: isVisibilityInvalid ? ValueType.NA : visibility ? visibility.trend : ValueType.NA,
              sortValue: visibility?.value || 0,
            },
            sentiment: {
              containerStyle: sentimentContainerStyle,
              value: isSentimentInvalid ? ValueType.NA : addPercent(sentiment.value),
              trend: isSentimentInvalid ? ValueType.NA : sentiment.sentiment,
              sortValue: sentiment?.value || 0
            },
            topCompetitors: topic.topCompetitors,
        };
      }) || []
    };
  }

  private getColDef(): ColDef[] {
    const valueStyle = { display: 'flex', justifyContent: 'center', alignItems: 'center', width: '33px' };
    const trendStyle = { display: 'flex', justifyContent: 'center', alignItems: 'center', width: '53px' };
    const trendConfig = { size: 'small' };
    return [
      {
        headerName: 'Topic',
        headerClass: 'topic-column',
        field: 'topic',
        width: 316,
        cellStyle: { fontWeight: '600' },
        headerComponent: TableHeaderCellComponent,
      },
      {
        headerName: 'Brand Visibility',
        headerClass: 'visibility-column',
        field: 'visibility',
        width: 317,
        headerComponent: TableHeaderCellComponent,
        headerComponentParams: {
          sortable: true,
          field: 'visibility',
          tableApi: this.gridApi
        },
        comparator: (valueA: any, valueB: any) => {
          const numA = valueA?.sortValue ?? 0;
          const numB = valueB?.sortValue ?? 0;
          return numA - numB;
        },
        cellRenderer: PerformanceTrendRendererComponent,
        cellRendererParams: {
          valueStyle: valueStyle,
          trendConfig: trendConfig,
          trendStyle: trendStyle,
        }
      },
      {
        headerName: 'AI Sentiment',
        headerClass: 'ai-sentiment-column',
        field: 'sentiment',
        width: 180,
        comparator: (valueA: any, valueB: any) => {
          const numA = valueA?.sortValue ?? 0;
          const numB = valueB?.sortValue ?? 0;
          return numA - numB;
        },
        headerComponent: TableHeaderCellComponent,
        headerComponentParams: {
          sortable: true,
          field: 'sentiment',
          tableApi: this.gridApi
        },
        cellRenderer: PerformanceTrendRendererComponent,
        cellRendererParams: {
          valueStyle: valueStyle,
          trendConfig: trendConfig,
          trendStyle: trendStyle,
        }
      },
      {
        headerName: 'Top Competitors',
        headerClass: 'top-competitors-column',
        field: 'topCompetitors',
        width: 317,
        valueFormatter: this.topCompetitorsFormatter,
        headerComponent: TableHeaderCellComponent,
      },
    ];
  }

  private topCompetitorsFormatter(params: any): string {
    return params.value?.join(', ');
  }

  protected onSortChanged(event: SortClickEvent): void {
    if (!this.gridApi) return;

    const sortDirection = event.direction === 'asc' ? 'asc' : event.direction === 'desc' ? 'desc' : undefined;
    this.gridApi.applyColumnState({
      state: [{
        colId: event.field,
        sort: sortDirection
      }]
    });
    this.gridApi.refreshCells();
  }

}
